
import router from '@/router';
import store from '@/store';
import getMeetingDateAndTime from '@/logic/helpers/dates/getMeetingDateAndTime';
import AvatarProgressCircle from '@/components/AvatarProgressCircle.vue';
import currentAssessmentService from '@/logic/services/currentAssessmentService';

export default {
  name: 'AssessmentProgressRow',

  components: {
    AvatarProgressCircle,
  },

  props: {
    mode: {
      type: String,
      default: '',
    },
    company: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    position: {
      type: String,
      default: '',
    },
    info: {
      type: String,
      default: '',
    },
    progressPercentage: {
      type: Number,
      default: 0,
    },
    percentage: {
      type: Number,
      default: 0,
    },
    imgUrl: {
      type: String,
      default: '',
    },
    shouldRouteToCandidatePage: Boolean,
    candidateId: {
      type: String,
      default: '',
    },
    assessmentId: {
      type: String,
      default: '',
    },
    status: {
      type: String,
      default: '',
    },
    assessorStatus: {
      type: String,
      default: '',
    },
    tasksView: Boolean,
    date: {
      type: String,
      default: '',
    },
    canShowTheTimeline: Boolean,
    canOpenInterviewsModalAsAssessor: Boolean,
    id: {
      type: String,
      default: '',
    },
    deadline: {
      type: String,
      default: '',
    },
    assessmentObject: {
      type: Object,
      default: (): Record<string, never> => ({}),
    },
    coAssessors: {
      type: Array,
      default: (): unknown[] => [],
    },
    userWhoCreated: {
      type: String,
      default: '',
    },
    address: {
      type: Object,
      default: (): Record<string, never> => ({}),
    },
  },

  data(): Record<string, unknown> {
    return {
      device: store.state.device,
      userRole: store.state.userRole,
      getMeetingDateAndTime,
      applyProjectRowFocusClass: false,
      visitedStyle: false,
      eventHighlightedClassName: '',
    };
  },

  computed: {
    initialsToPass(): string {
      if (this.company && !this.name) return this.company[0].toUpperCase();
      else if (this.name) {
        const name = this.name.split(' ').filter((partOfName: string) => partOfName !== '');
        return `${name[0][0].toUpperCase()}${name[1][0].toUpperCase()}`;
      }
      return 'N/A';
    },

    adjustAvatarToView(): string {
      if (!this.tasksView) return 'margin-right: 0;';
      return '';
    },

    projectIsEditableByClient(): boolean {
      const projectNotProposedToAnyAssessor =
        this.status === 'draft' || this.status === 'new' || this.status === 'assessors_matched';
      return (
        this.userRole === 'client' && this.mode === 'projects' && projectNotProposedToAnyAssessor
      );
    },

    projectsRequestPending(): boolean {
      return store.state.projectsRequestPending;
    },

    clientsAssessment(): boolean {
      return this.userRole === 'client' && this.mode === 'interviews';
    },

    userHasToMakeAction(): boolean {
      let actionOnProject, actionOnAssessment, actionOnReport;

      if (this.mode === 'interviews') {
        if (this.userRole === 'client') return false;

        const correspondingStatuses = [
          'new',
          'accepted',
          'cancelled_by_candidate',
          'in_progress',
          'snoozed',
          'assessed',
        ];

        actionOnAssessment = correspondingStatuses.includes(this.status);
      } else if (this.mode === 'projects') {
        actionOnProject =
          (this.assessorStatus === 'invited' && this.status !== 'cancelled') ||
          (this.userRole === 'client' && this.status === 'accepted');
      } else if (this.mode === 'reports') {
        if (this.userRole === 'assessor') {
          const correspondingReportStatuses = ['todo', 'rejected'];

          actionOnReport =
            correspondingReportStatuses.includes(this.assessmentObject?.report?.status) ||
            this.status === 'assessed';
        } else actionOnReport = this.assessmentObject?.report?.status === 'published';
      }
      const isExpired = this.assessorStatus === 'invited' && Date.parse(this.deadline) < Date.now();

      return (actionOnProject || actionOnAssessment || actionOnReport) && !isExpired;
    },

    allActionsCompleted(): boolean {
      if (this.assessmentObject?.report?.status === 'rejected') return;

      const assessorDidAllActions = ['invited', 'confirmed', 'published'];

      return (
        this.assessorStatus === 'accepted' ||
        (this.status === 'pending' && this.userRole === 'client') ||
        (assessorDidAllActions.includes(this.status) &&
          this.mode !== 'projects' &&
          this.userRole === 'assessor')
      );
    },

    modalCanBeOpened(): boolean {
      return (
        this.device === 'desktop' &&
        (this.tasksView || this.canOpenInterviewsModalAsAssessor) &&
        !this.canShowTheTimeline
      );
    },

    isAuthorizedAssessorACoAssessor(): boolean {
      return (
        this.coAssessors?.filter((coAssessor) => {
          return coAssessor.id === store.state.userId;
        }).length === 1
      );
    },
  },

  watch: {
    projectsRequestPending(newVal: unknown): void {
      if (!newVal) {
        setTimeout(() => {
          this.focusOnProjectIfNeeded();
        }, 200);
      }
    },

    isAuthorizedAssessorACoAssessor(newVal: boolean): void {
      if (newVal) {
        this.eventHighlightedClassName = 'event--co-assessor';
        return;
      }
    },
  },

  created(): void {
    if (this.mode === 'projects') {
      this.eventHighlightedClassName = 'event--transparent';
    }
    if (this.mode === 'interviews') this.eventHighlightedClassName = 'event--blue';
    if (this.mode === 'reports') this.eventHighlightedClassName = 'event--gray';
    this.updateClock();
  },

  mounted(): void {
    if (!this.projectsRequestPending) {
      setTimeout(() => {
        this.focusOnProjectIfNeeded();
      });
    }
  },
  beforeDestroy(): void {
    clearInterval(this.timer);
  },

  methods: {
    updateClock(): void {
      this.timer = setInterval(() => {
        this.currentTime = new Date();
        if (this.timeRemaining === 0) {
          clearInterval(this.timer);
        }
      }, 1000);
    },
    emitOpenModal(): void {
      if (this.modalCanBeOpened) {
        store.commit('setModalOpen', true);
        this.$emit('openModal');
      }
    },

    emitGoToTheTimeline(): void {
      if ((this.device === 'mobile' || !this.tasksView) && this.userRole === 'client') {
        this.$emit('goToTheTimeline');
      }
    },

    makeProperRedirectionBasedOnMode(): void {
      let queryToPass: Record<string, string>;

      if (this.mode === 'interviews' || this.mode === 'reports') {
        queryToPass = {
          candidateID: this.candidateId,
          assessmentID: this.assessmentId,
          company: this.company,
          position: this.position,
          status: this.status,
          allowsVideo: this.allowsVideo,
        };
      }

      if (this.mode === 'interviews') {
        currentAssessmentService.saveToLocalStorage(this.assessmentObject);
      }

      if (this.mode === 'projects') this.$emit('redirToProjectDetails');

      if (this.mode === 'interviews') {
        if (
          this.userRole !== 'client' &&
          this.shouldRouteToCandidatePage &&
          (this.device === 'mobile' || !this.tasksView)
        ) {
          router.push({ path: '/assessor-candidate', query: queryToPass });
        } else this.$emit('changeStage');
      }

      if (this.mode === 'reports' && (this.device === 'mobile' || !this.tasksView)) {
        router.push({ path: '/assessor-report', query: queryToPass });
      }
    },

    parseStatus(status: string): string {
      if (status === 'in_progress') {
        status = 'inProgress';
      }
      if (status === 'request_expired') status = 'expired';

      return status;
    },

    focusOnProjectIfNeeded(): void {
      if (
        store.state.assessmentsFilter.activeMode === 'projects' &&
        store.state.recentlyEditedProjectId === this.id
      ) {
        this.applyProjectRowFocusClass = true;

        const topOfRow = this.$refs.assessmentProgressRow.getBoundingClientRect().top;
        scrollTo(0, topOfRow);

        store.commit('setRecentlyEditedProjectId', undefined);

        this.applyVisitedStyle();
      }
    },

    applyVisitedStyle(): void {
      if (this.isAuthorizedAssessorACoAssessor) return;

      switch (this.userWhoCreated) {
        case 'client':
        case 'assessor':
          if (
            this.$el.classList.contains('event--green') ||
            this.$el.classList.contains('event--light--green') ||
            this.$el.classList.contains('event--dark--blue') ||
            this.$el.classList.contains('event--light--blue')
          ) {
            this.addPopUpEffect(this.$refs.assessmentProgressRow);
          }
          break;
      }
      // first remove recently applied style
      const lastVisitedItem = document.querySelector(`.${this.eventHighlightedClassName}`);
      const shouldNotApply =
        !lastVisitedItem ||
        (this.visitedStyle && this.$vnode.elm.className.includes(this.eventHighlightedClassName));

      if (shouldNotApply) return;

      lastVisitedItem.classList.remove(this.eventHighlightedClassName);

      this.visitedStyle = false;

      setTimeout(() => {
        this.visitedStyle = true;
      });
    },

    addPopUpEffect(element: HTMLElement | null): void {
      if (element) {
        element.classList.add('pop-up');
        setTimeout(() => {
          element.classList.remove('pop-up');
        }, 300);
      }
    },

    getEventClass(): Record<string, unknown> {
      let cssClass;

      if (this.isAuthorizedAssessorACoAssessor) {
        cssClass = {};
        cssClass['event--co-assessor'] = true;
        return cssClass;
      }
      if (this.mode === 'projects')
        cssClass = {
          'assessment-progress-row--initial-highlight': this.applyProjectRowFocusClass,
          'event--transparent': this.visitedStyle,
          'event--green': this.userWhoCreated === 'client',
          'event--light--green': this.userWhoCreated === 'assessor',
        };
      if (this.mode === 'interviews' && this.tasksView)
        cssClass = {
          'assessment-progress-row--initial-highlight': this.applyProjectRowFocusClass,
          'event--blue': this.visitedStyle,
          'event--dark--blue': this.userWhoCreated === 'client',
          'event--light--blue': this.userWhoCreated === 'assessor',
        };
      if (this.mode === 'reports')
        cssClass = {
          'assessment-progress-row--initial-highlight': this.applyProjectRowFocusClass,
          'event--blue': this.visitedStyle,
          'event--dark--blue': this.userWhoCreated === 'client',
          'event--light--blue': this.userWhoCreated === 'assessor',
        };
    },

    makeActionOnClick(): void {
      if (this.modalOpened === true) {
        return;
      } else {
        const isReport = this.mode === 'reports';
        const hasVirtualAssessmentReports = this.assessmentObject.report?.virtual_assessment_reports
          ?.length;
        const userIsAssessor = this.userRole === 'assessor';

        if (isReport && hasVirtualAssessmentReports && userIsAssessor) {
          this.redirToDetailedReport();
          return;
        } else {
          this.makeProperRedirectionBasedOnMode();
          this.emitOpenModal();
          this.emitGoToTheTimeline();
        }
      }
    },

    redirToDetailedReport(): void {
      const virtualAssessmentReportPath = this.assessmentObject.report
        .virtual_assessment_reports[0];

      router.push({
        path: '/detailed-report',
        query: {
          candidateId: this.assessmentObject.candidate.id,
          reportId: this.assessmentObject.report.id,
          virtualAssessmentReportPath,
          forCompany: this.company,
          assessmentDate: this.date,
        },
      });
    },
  },
};
